import { Injectable, signal, computed } from '@angular/core';

/**
 * Represents details of a font asset.
 */
export interface Asset {
  /** The URL of the asset file. */
  name: string;

  /** The time taken to load the asset, in milliseconds. */
  duration: number;

  /** The size of the asset file transferred, in bytes. */
  transferSize: number;
}

/**
 * Represents the state of font assets in the application.
 */
interface AssetStateModel {
  /** Indicates whether all fonts have been successfully loaded. */
  fontsLoaded: boolean;

  /** List of loaded font assets. */
  fonts: Asset[];
}

/**
 * A service to monitor and track the loading state of font assets.
 */
@Injectable({ providedIn: 'root' })
export class AssetMonitorStoreService {
  /** Internal state to track the font loading process. */
  private _state = signal<AssetStateModel>({
    fontsLoaded: false,
    fonts: [],
  });

  /** Publicly accessible computed state of font assets. */
  readonly $state = this._state.asReadonly();
  readonly $fontLoaded = computed(() => {
    return this.$state().fontsLoaded;
  });
  constructor() {
    // Initialize the monitoring of font assets when the service is created.
    this.init();
  }

  /**
   * Initializes the monitoring of font assets by checking existing loaded fonts
   * and setting up a `PerformanceObserver` to track new font resource loads.
   */
  private init(): void {
    // Check for fonts that are already loaded when the application starts.
    const existingFonts = this.getExistingFonts();

    // If any fonts are already loaded, update the internal state.
    if (existingFonts.length > 0) {
      this.updateFontState(existingFonts, true);
    }

    // Setup a `PerformanceObserver` to monitor new font loads.
    this.observeNewFonts();
  }

  /**
   * Retrieves existing font resources from the `Performance` API.
   * @returns An array of font assets that are already loaded.
   */
  private getExistingFonts(): Asset[] {
    return (
      performance.getEntriesByType('resource') as PerformanceResourceTiming[]
    )
      .filter((entry) => entry.initiatorType === 'font') // Filter only font-type entries.
      .map(this.mapResource);
  }

  private observeNewFonts(): void {
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver(
        (list: PerformanceObserverEntryList) => {
          // Collect new font resources.
          const newFonts: Asset[] = list
            .getEntries() // Get all entries in the observer's buffer.
            .filter(
              (entry): entry is PerformanceResourceTiming =>
                entry instanceof PerformanceResourceTiming &&
                /\.(ttf|woff|woff2|eot|otf)$/i.test(entry.name)
            )
            .map(this.mapResource); // Map them to the `FontAsset` structure.

          // If new fonts are found, update the state.
          if (newFonts.length > 0) {
            this.updateFontState(newFonts, true);
          }
        }
      );

      // Observe resource loads, including buffered entries as the page reloads.
      observer.observe({ type: 'resource', buffered: true });
    }
  }

  private mapResource(resource: PerformanceResourceTiming): Asset {
    return {
      name: resource.name,
      duration: resource.duration,
      transferSize: resource.transferSize ?? 0,
    };
  }

  /**
   * Updates the internal state with newly loaded fonts.
   *
   * @param newFonts - An array of the newly loaded font assets.
   * @param fontsLoaded - Whether the font loading process is complete.
   */
  private updateFontState(newFonts: Asset[], fontsLoaded: boolean): void {
    this._state.update((state) => ({
      ...state,
      fonts: [...state.fonts, ...newFonts],
      fontsLoaded,
    }));
  }
}
